import React from 'react';
import { useNavigate } from 'react-router';
import { Bell, Search, User, LogOut, Menu } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import FormControl from '../../components/common/FormControl';

interface AnalystHeaderProps {
    onToggleSidebar?: () => void;
}

const AnalystHeader: React.FC<AnalystHeaderProps> = ({ onToggleSidebar }) => {
    const { logout, state } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    return (
        <header className="analyst-header" style={{
            height: '70px',
            background: 'linear-gradient(135deg, #be123c 0%, #9f1239 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 24px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
            zIndex: 1000
        }}>
            <div className="flex items-center gap-4">
                <button
                    data-testid="BTN_analyst_header_toggle_sidebar"
                    onClick={onToggleSidebar}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white"
                    title="Toggle Menu"
                >
                    <Menu size={24} />
                </button>

                {/* Logo */}
                <div style={{ height: '70px', display: 'flex', alignItems: 'center' }}>
                    <div style={{
                        backgroundColor: '#fff1f2', // Light Red Tint
                        borderRadius: '50px',
                        padding: '0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '44px',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                        overflow: 'hidden'
                    }}>
                        <img
                            src={process.env.PUBLIC_URL + '/anygrc-logo.png'}
                            alt="AnyGRC Analyst"
                            style={{
                                height: '100%',
                                width: 'auto',
                                objectFit: 'contain',
                                mixBlendMode: 'multiply',
                                filter: 'hue-rotate(135deg) saturate(1.2) brightness(0.9)' // Blue -> Red
                            }}
                        />
                    </div>
                </div>

                {/* Persona Icon */}
                <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center border border-white/10">
                    <span className="text-xl">📊</span>
                </div>

                {/* Persona Text */}
                <div>
                    <span className="text-sm font-bold text-red-100 uppercase tracking-wider">Risk Analyst</span>
                    {state.user?.entity_name && (
                        <span className="ml-3 pl-3 border-l border-red-100/30 text-red-50 font-medium">
                            {state.user.entity_name}
                        </span>
                    )}
                </div>
            </div>

            <div className="flex-1 max-w-xl mx-12">
                <FormControl.Input
                    type="text"
                    placeholder="Search risks, assessments, or controls..."
                    leadingIcon={<Search className="text-slate-400" size={18} />}
                    className="fi-input--header"
                />
            </div>

            <div className="flex items-center gap-6">
                <button className="relative text-slate-400 hover:text-white transition-colors">
                    <Bell size={20} />
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-rose-500 rounded-full"></span>
                </button>

                <div className="h-8 w-[1px] bg-slate-700"></div>

                <div className="flex items-center gap-3">
                    <div className="text-right hidden md:block">
                        <div className="text-sm font-semibold">{state.user?.user_name || 'Analyst'}</div>
                        <div className="text-xs text-slate-400">Risk Department</div>
                    </div>
                    <div className="w-10 h-10 bg-gradient-to-br from-slate-700 to-slate-900 rounded-full flex items-center justify-center shadow-lg border-2 border-slate-600">
                        <User size={20} className="text-white" />
                    </div>
                    <button
                        data-testid="BTN_analyst_header_logout"
                        onClick={handleLogout}
                        className="ml-2 text-slate-400 hover:text-white transition-colors"
                        title="Sign Out"
                    >
                        <LogOut size={18} />
                    </button>
                </div>
            </div>
        </header>
    );
};

export default AnalystHeader;
